import { computed, type WritableComputedRef } from "vue";
import { defineStore } from "pinia";
import {
  applyProjectFonts,
  restorePersistedFonts,
} from "../composables/useEditorFont";
import { assertNever } from "../lib/assertNever";
import {
  readBoolPref,
  readCollapsedActs,
  readCollapsedChapters,
  writeBoolPref,
} from "../lib/localPrefs";
import {
  fromMaybe,
  isJust,
  isNothing,
  just,
  nothing,
  toMaybe,
  type Maybe,
} from "../lib/maybe";
import { useTea } from "../lib/tea";
import {
  chapterFolderFromScenePath,
  databaseNameFromActivePath,
  defaultZwStatusBar,
  flattenChapters,
  noteNameFromActivePath,
  projectDisplayName,
  sceneFileFromScenePath,
  type ManuscriptItem,
  type ZwChapter,
  type ZwConfig,
} from "../lib/zwConfig";
import { isMapFileName } from "../lib/mapDocument";
import type {
  EditorActiveTab,
  EditorKind,
  EditorPayload,
} from "../lib/tauri/core";
import {
  initialEditorSessionModel,
  isMobileLayout,
  sessionActiveBasename,
  sessionFolderName,
  updateEditorSession,
  type ActiveSceneInfo,
  type EditorSessionCmd,
  type EditorSessionModel,
  type EditorSessionMsg,
  type SessionApplyOpts,
  type SessionFlagKey,
  type SessionPathKey,
  type TrashPreview,
} from "./useEditorSessionStore.update";
import { useEditorHeadingsStore } from "./useEditorHeadingsStore";
import { useProjectGoalsStore } from "./useProjectGoalsStore";

export type { ActiveSceneInfo, TrashPreview };

// The pure update module mirrors these unions (it may not import lib/tauri).
// Keep them assignable both ways so the mirror cannot drift.
type Assert<T extends true> = T;
export type _KindMirrorsCore = Assert<
  [EditorSessionModel["kind"]] extends [EditorKind]
    ? [EditorKind] extends [EditorSessionModel["kind"]]
      ? true
      : false
    : false
>;
export type _TabMirrorsCore = Assert<
  [
    NonNullable<
      Extract<EditorSessionModel["activeTab"], { tag: "just" }>["value"]
    >,
  ] extends [EditorActiveTab]
    ? [EditorActiveTab] extends [
        Extract<EditorSessionModel["activeTab"], { tag: "just" }>["value"],
      ]
      ? true
      : false
    : false
>;

function isEmbedSession(): boolean {
  return (
    typeof document !== "undefined" &&
    document.documentElement.dataset.zwEmbed === "1"
  );
}

/**
 * Editor session state (open project, active file, panels, draft text).
 *
 * TEA: pure Model/Msg in `useEditorSessionStore.update.ts`. Every field below
 * keeps its pre-TEA shape (readable, assignable) but is a writable computed
 * over one `model`; assignment dispatches a Msg. Cmds (prefs, fonts, goals,
 * Harper) run after the model write (`useTea` / Elm order).
 */
export const useEditorSessionStore = defineStore("editorSession", () => {
  const embed = isEmbedSession();

  function runCmd(cmd: EditorSessionCmd): void {
    switch (cmd.kind) {
      case "persistBoolPref":
        writeBoolPref(cmd.name, cmd.value);
        return;
      case "applyFonts":
        applyProjectFonts(cmd.fonts);
        return;
      case "restoreFonts":
        restorePersistedFonts();
        return;
      case "scheduleHarper":
        useEditorHeadingsStore().scheduleHarperLexiconRefresh();
        return;
      case "goalsNavigation":
        useProjectGoalsStore().noteActiveChapterSaved(
          cmd.relativePath,
          cmd.doc,
        );
        return;
      case "goalsEnsureIfNeeded":
        useProjectGoalsStore().ensureGoalProgressIfNeeded();
        return;
      case "goalsResetWordCounts":
        useProjectGoalsStore().resetWordCounts();
        return;
      case "goalsRootChanged": {
        const goals = useProjectGoalsStore();
        goals.resetSessionBaseline();
        void goals
          .refreshManuscriptWordCounts()
          .then(() => goals.ensureGoalProgress());
        return;
      }
      case "goalsRefresh": {
        const goals = useProjectGoalsStore();
        void goals
          .refreshManuscriptWordCounts()
          .then(() => goals.ensureGoalProgress());
        return;
      }
      default:
        assertNever(cmd);
    }
  }

  const { model, dispatch: teaDispatch } = useTea(
    initialEditorSessionModel({
      outlineCollapsed: embed ? false : readBoolPref("outlineCollapsed"),
      contextPanelCollapsed: embed
        ? false
        : readBoolPref("contextPanelCollapsed"),
      databaseGraphView: embed ? false : readBoolPref("databaseGraphView"),
      initialWidth: typeof window === "undefined" ? 0 : window.innerWidth,
    }),
    updateEditorSession,
    { runCmd: just(runCmd) },
  );

  function dispatch(msg: EditorSessionMsg): void {
    void teaDispatch(msg);
  }

  /**
   * Apply an editor open/reload payload into session (and fonts/goals/Harper
   * Cmds). Replaces the old `registerApplyPayload` callback hole.
   */
  function applyPayload(
    payload: EditorPayload,
    opts: { keepDoc?: boolean; navigationOnly?: boolean } = {},
  ): void {
    const previousRoot = model.value.rootPath;
    const rootChanged = fromMaybe(payload.rootPath) !== fromMaybe(previousRoot);
    const applyOpts: SessionApplyOpts = {
      keepDoc: opts.keepDoc === true,
      navigationOnly: opts.navigationOnly === true,
    };
    let collapsedChapters = model.value.collapsedChapters;
    let collapsedActs = model.value.collapsedActs;
    if (rootChanged) {
      if (isJust(payload.rootPath)) {
        collapsedChapters = readCollapsedChapters(payload.rootPath.value);
        collapsedActs = readCollapsedActs(payload.rootPath.value);
      } else {
        collapsedChapters = [];
        collapsedActs = [];
      }
    }
    dispatch({
      type: "ApplyPayload",
      payload: {
        kind: payload.kind,
        rootPath: payload.rootPath,
        activePath: payload.activePath,
        content: payload.content,
        project: payload.project,
        synopsis: payload.synopsis,
        activeTab: payload.activeTab,
      },
      opts: applyOpts,
      collapsedChapters,
      collapsedActs,
      rootChanged,
    });
  }

  function flag(key: SessionFlagKey): WritableComputedRef<boolean> {
    return computed({
      get: () => model.value[key],
      set: (value) => dispatch({ type: "SetFlag", key, value }),
    });
  }

  function pathField(key: SessionPathKey): WritableComputedRef<Maybe<string>> {
    return computed({
      get: () => model.value[key],
      set: (value) => dispatch({ type: "SetPath", key, value }),
    });
  }

  const ready = flag("ready");
  /** Native Android/iOS shell (set once at boot from `isMobileShell()`). */
  const nativeMobileShell = flag("nativeMobileShell");
  /** Mobile layout: native shell or a shell narrower than 768px (ZW-276).
   * Derived, so it is read-only; width changes go through `measureShellWidth`. */
  const mobileShell = computed(() => isMobileLayout(model.value));

  function measureShellWidth(width: number): void {
    dispatch({ type: "ShellWidthMeasured", width });
  }
  const outlineOpen = flag("outlineOpen");
  const contextPanelOpen = flag("contextPanelOpen");
  const outlineCollapsed = flag("outlineCollapsed");
  const contextPanelCollapsed = flag("contextPanelCollapsed");
  const storyboardView = flag("storyboardView");
  /** Database tab: relationship graph canvas vs entry/map editor (#72). */
  const databaseGraphView = flag("databaseGraphView");
  const commentsOpen = flag("commentsOpen");
  const historyOpen = flag("historyOpen");
  const goalsOpen = flag("goalsOpen");
  /** Project-wide search dialog (command-palette style). */
  const projectSearchOpen = flag("projectSearchOpen");
  /** Thesaurus dialog when not on the manuscript rail. */
  const thesaurusDialogOpen = flag("thesaurusDialogOpen");
  const dirty = flag("dirty");
  const suppressDirty = flag("suppressDirty");

  const doc = computed({
    get: () => model.value.doc,
    set: (value: string) => dispatch({ type: "SetDoc", value }),
  });
  const synopsis = computed({
    get: () => model.value.synopsis,
    set: (value: string) => dispatch({ type: "SetSynopsis", value }),
  });
  const kind = computed({
    get: (): EditorKind => model.value.kind,
    set: (value: EditorKind) => dispatch({ type: "SetKind", value }),
  });
  const rootPath = pathField("rootPath");
  const activePath = pathField("activePath");
  const activeTab = computed({
    get: (): Maybe<EditorActiveTab> => model.value.activeTab,
    set: (value: Maybe<EditorActiveTab>) =>
      dispatch({ type: "SetActiveTab", value }),
  });
  /** Collapsed chapters, keyed by `chapterPrimaryPath`. Browser localStorage
   * per project root (not `.zwlocal`); see `localPrefs`. */
  const collapsedChapters = computed({
    get: () => model.value.collapsedChapters,
    set: (value: string[]) => dispatch({ type: "SetCollapsedChapters", value }),
  });
  /** Collapsed acts, keyed by `actPrimaryPath` (#91). Shared live between the
   * outline and the Storyboard, same as `collapsedChapters`. */
  const collapsedActs = computed({
    get: () => model.value.collapsedActs,
    set: (value: string[]) => dispatch({ type: "SetCollapsedActs", value }),
  });
  const notesLastFile = pathField("notesLastFile");
  const databaseLastFile = pathField("databaseLastFile");
  /** Last manuscript-tab file (scene) - Pinia session sticky only.
   * Not persisted to `.zwlocal` (resume uses `lastRoute`). */
  const manuscriptLastFile = pathField("manuscriptLastFile");
  const projectConfig = computed({
    get: () => model.value.projectConfig,
    set: (value: Maybe<ZwConfig>) =>
      dispatch({ type: "SetProjectConfig", value }),
  });
  // Retain transformed asset URLs when the editor remounts on navigation.
  const imagePreviewRevision = computed({
    get: () => model.value.imagePreviewRevision,
    set: (value: number) =>
      dispatch({ type: "SetImagePreviewRevision", value }),
  });
  const activeCommentId = pathField("activeCommentId");
  const trashPreview = computed({
    get: () => model.value.trashPreview,
    set: (value: Maybe<TrashPreview>) =>
      dispatch({ type: "SetTrashPreview", value }),
  });
  const trashRestoringPath = pathField("trashRestoringPath");
  const editorBottomInset = computed({
    get: () => model.value.editorBottomInset,
    set: (value: number) => dispatch({ type: "SetEditorBottomInset", value }),
  });

  /** The manuscript order as-is: acts and ungrouped chapters (#91). */
  const manuscript = computed<ManuscriptItem[]>(() =>
    isJust(projectConfig.value) ? projectConfig.value.value.manuscript : [],
  );

  /** Every chapter in manuscript order, act nesting flattened away. What
   * anything act-agnostic (export, goals, search, graph) should read. Only the
   * outline, Storyboard, and drag-and-drop need `manuscript` itself. */
  const chapters = computed<ZwChapter[]>(() =>
    flattenChapters(manuscript.value),
  );

  const isApple = computed(
    () =>
      /Mac|iPhone|iPad|iPod/.test(navigator.platform) ||
      navigator.userAgent.includes("Mac"),
  );
  const modLabel = computed(() => (isApple.value ? "⌘" : "Ctrl+"));
  const altLabel = computed(() => (isApple.value ? "⌥" : "Alt+"));

  const folderName = computed(() => sessionFolderName(model.value));

  const projectName = computed(() =>
    projectDisplayName(projectConfig.value, folderName.value),
  );

  /** Path of the active file relative to the project root (e.g.
   * `Chapters/Chapter 1/scene-3f9a1.md`). A renamed scene can share a basename
   * with one in another chapter, so identity is the full relative path. */
  const activeBasename = computed((): Maybe<string> =>
    sessionActiveBasename(model.value),
  );

  const activeNote = computed(() => noteNameFromActivePath(activePath.value));

  const activeDatabase = computed(() =>
    databaseNameFromActivePath(activePath.value),
  );

  const activeMap = computed(
    () =>
      isJust(activeDatabase.value) && isMapFileName(activeDatabase.value.value),
  );

  const outlineActiveFile = computed((): Maybe<string> =>
    isJust(activeNote.value) || isJust(activeDatabase.value)
      ? nothing
      : activeBasename.value,
  );

  const hideGoalsChip = computed(() => activeMap.value || storyboardView.value);

  const editingSidecar = computed(
    () => isJust(activeNote.value) || isJust(activeDatabase.value),
  );

  /** Project fonts live in project settings; the ribbon never shows font picks. */
  const showRibbonFonts = computed(() => false);

  const statusBarPrefs = computed(() => {
    const base = defaultZwStatusBar();
    if (isNothing(projectConfig.value)) return base;
    const statusBar = projectConfig.value.value.statusBar;
    return { ...base, ...statusBar };
  });

  const isProject = computed(() => true);

  const statusBarPrefsEffective = computed(() => {
    const prefs = statusBarPrefs.value;
    if (activeMap.value || storyboardView.value) {
      return { ...prefs, wordCount: false, readingTime: false };
    }
    return prefs;
  });

  const activeSceneInfo = computed<Maybe<ActiveSceneInfo>>(() => {
    const basename = activeBasename.value;
    if (isNothing(basename)) return nothing;
    const folder = chapterFolderFromScenePath(basename.value);
    const sceneFile = sceneFileFromScenePath(basename.value);
    if (isNothing(folder) || isNothing(sceneFile)) return nothing;
    const chapter = toMaybe(
      chapters.value.find((c) => c.folder === folder.value),
    );
    if (isNothing(chapter)) return nothing;
    return just({
      folder: folder.value,
      sceneFile: sceneFile.value,
      chapter: chapter.value,
    });
  });

  return {
    ready,
    nativeMobileShell,
    mobileShell,
    measureShellWidth,
    outlineOpen,
    outlineCollapsed,
    contextPanelOpen,
    contextPanelCollapsed,
    commentsOpen,
    historyOpen,
    goalsOpen,
    projectSearchOpen,
    thesaurusDialogOpen,
    doc,
    synopsis,
    kind,
    rootPath,
    activePath,
    activeTab,
    collapsedChapters,
    collapsedActs,
    notesLastFile,
    databaseLastFile,
    manuscriptLastFile,
    projectConfig,
    manuscript,
    chapters,
    dirty,
    imagePreviewRevision,
    suppressDirty,
    activeCommentId,
    trashPreview,
    trashRestoringPath,
    editorBottomInset,
    folderName,
    projectName,
    activeBasename,
    activeNote,
    activeDatabase,
    activeMap,
    outlineActiveFile,
    editingSidecar,
    hideGoalsChip,
    showRibbonFonts,
    statusBarPrefs,
    statusBarPrefsEffective,
    isApple,
    modLabel,
    altLabel,
    isProject,
    activeSceneInfo,
    storyboardView,
    databaseGraphView,
    applyPayload,
  };
});
